import { z } from 'zod';
import { uuidSchema } from './common';

/**
 * The WayAI Guide (wayai#4948): ONE production chat hub, owned by WayAI, whose agent answers
 * questions about the platform. `platform_config.guide_hub_id` names it; unset, the Guide is off —
 * nobody is enrolled, and no client shows its help entry.
 *
 * ENROLLMENT makes a person an end user of the Guide hub, never by a snapshot build or a page
 * render:
 *   - a new account, when it is first provisioned (`provisionUser`, every client's arrival path);
 *   - any signed-in person, through `POST /api/guide/access` (the help entry, "Ask WayAI").
 * A person the Guide's staff removed is not enrolled again: the route answers 404, the Guide being
 * unavailable to them. What an enrollment writes, and each stage of a person's lifecycle on the
 * Guide (the staff's Users tab, removal, re-add, the account's deletion), is stated once, in the
 * backend's `utils/guide-enrollment.ts`.
 *
 * The session snapshot carries `guide_hub_id` while it is set (`SessionSnapshot.guide_hub_id`);
 * the clients land a person with nowhere else to go in the Guide, and no environment filter hides
 * it (`isGuideHub` in `@wayai/state`).
 */

/** POST /api/guide/access — 200: the Guide hub the caller is (now) an end user of. */
export interface GuideAccessResponse {
  data: { hub_id: string };
}

/**
 * What `PUT`/`GET /api/admin/guide` reports of a hub: the facts `set-hub` admits it on (a
 * production chat hub with an enabled app channel), and its owning org, which the operator
 * confirms.
 */
export interface AdminGuideHub {
  hub_id: string;
  hub_name: string | null;
  hub_type: string | null;
  hub_environment: string | null;
  app_channel_enabled: boolean;
  organization_id: string;
  organization_name: string | null;
}

/**
 * PUT /api/admin/guide — make `hub_id` the Guide. Refused (409, `details.reason`
 * `GUIDE_HUB_INELIGIBLE_REASON`) unless the hub is a production chat hub with an enabled app
 * channel. `dry_run` reports the hub without writing anything.
 */
export const adminSetGuideHubBody = z
  .object({
    hub_id: uuidSchema,
    dry_run: z.boolean().optional(),
  })
  .strict();

export type AdminSetGuideHubBody = z.input<typeof adminSetGuideHubBody>;

/** The `details.reason` of a `PUT /api/admin/guide` refused for the hub it names. */
export const GUIDE_HUB_INELIGIBLE_REASON = 'guide_hub_ineligible';

/** PUT /api/admin/guide — 200: the hub, and whether it was written (false on a dry run). */
export interface AdminSetGuideHubResponse {
  data: { hub: AdminGuideHub; written: boolean };
}

/**
 * GET /api/admin/guide — 200: the Guide as configured. `hub` is null while it is unset, and when
 * the hub it names can no longer be read (deleted).
 */
export interface AdminGuideStatusResponse {
  data: { guide_hub_id: string | null; hub: AdminGuideHub | null };
}

/** DELETE /api/admin/guide — 200: the Guide is off; nobody new is enrolled. */
export interface AdminClearGuideHubResponse {
  data: { guide_hub_id: null };
}
